/**
 * Complete Job Role Definitions for JobAspirant
 * 63 roles organized by category
 * Indicates which roles have LIVE API data vs AI-generated data
 */

export const JOB_CATEGORIES = [
  {
    id: 'ai-ml',
    name: 'AI & Machine Learning',
    icon: 'Brain',
    description: 'Cutting-edge AI/ML roles',
    color: 'blue'
  },
  {
    id: 'data',
    name: 'Data & Analytics',
    icon: 'BarChart3',
    description: 'Data engineering and analysis',
    color: 'purple'
  },
  {
    id: 'frontend',
    name: 'Frontend Development',
    icon: 'Monitor',
    description: 'UI/UX and frontend frameworks',
    color: 'green'
  },
  {
    id: 'backend',
    name: 'Backend Development',
    icon: 'Server',
    description: 'Server-side and APIs',
    color: 'orange'
  },
  {
    id: 'fullstack',
    name: 'Full Stack',
    icon: 'Code',
    description: 'End-to-end development',
    color: 'pink'
  },
  {
    id: 'mobile',
    name: 'Mobile Development',
    icon: 'Smartphone',
    description: 'iOS, Android, cross-platform',
    color: 'indigo'
  },
  {
    id: 'cloud-devops',
    name: 'Cloud & DevOps',
    icon: 'Cloud',
    description: 'Infrastructure and deployment',
    color: 'cyan'
  },
  {
    id: 'emerging',
    name: 'Emerging Tech',
    icon: 'Zap',
    description: 'Blockchain, AR/VR, IoT',
    color: 'yellow'
  },
  {
    id: 'other',
    name: 'Specialized Roles',
    icon: 'Briefcase',
    description: 'UI/UX, API, Security',
    color: 'gray'
  }
];

// Top roles with LIVE LinkedIn API data (via RapidAPI cron job)
export const LIVE_API_ROLES = [
  'Backend Developer',
  'Full Stack Developer',
  'Python Developer',
  'Node.js Developer'
];

export const JOB_ROLES = [
  // ========================================
  // AI & MACHINE LEARNING (9 roles)
  // ========================================
  {
    id: 'genai-developer',
    title: 'GenAI Developer',
    category: 'ai-ml',
    description: 'Build and deploy generative AI applications using LLMs and modern frameworks',
    icon: 'Sparkles',
    avgSalary: '₹15-25L',
    keySkills: ['Python', 'LangChain', 'OpenAI API', 'Vector Databases', 'Prompt Engineering', 'RAG'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'junior-to-mid'
  },
  {
    id: 'ai-engineer',
    title: 'AI Engineer',
    category: 'ai-ml',
    description: 'Develop intelligent systems and machine learning solutions for real-world problems',
    icon: 'Cpu',
    avgSalary: '₹12-20L',
    keySkills: ['Python', 'TensorFlow', 'PyTorch', 'Computer Vision', 'NLP', 'MLOps'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'ml-engineer',
    title: 'Machine Learning Engineer',
    category: 'ai-ml',
    description: 'Design and implement machine learning models and data pipelines at scale',
    icon: 'GitBranch',
    avgSalary: '₹10-18L',
    keySkills: ['Python', 'Scikit-learn', 'Pandas', 'SQL', 'AWS/GCP', 'Docker'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'llm-engineer',
    title: 'LLM Engineer',
    category: 'ai-ml',
    description: 'Specialize in large language models and transformer architectures',
    icon: 'MessageSquare',
    avgSalary: '₹18-30L',
    keySkills: ['Transformers', 'Hugging Face', 'BERT', 'GPT', 'Fine-tuning', 'Python'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid-to-senior'
  },
  {
    id: 'nlp-engineer',
    title: 'NLP Engineer',
    category: 'ai-ml',
    description: 'Build natural language processing systems and applications',
    icon: 'FileText',
    avgSalary: '₹9-18L',
    keySkills: ['NLP', 'spaCy', 'NLTK', 'Transformers', 'Python', 'Deep Learning'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'computer-vision-engineer',
    title: 'Computer Vision Engineer',
    category: 'ai-ml',
    description: 'Develop computer vision and image processing solutions',
    icon: 'Eye',
    avgSalary: '₹10-20L',
    keySkills: ['OpenCV', 'PyTorch', 'TensorFlow', 'CNN', 'Object Detection', 'Python'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'junior-mlops-engineer',
    title: 'Junior MLOps Engineer',
    category: 'ai-ml',
    description: 'Entry-level role in ML operations and model deployment',
    icon: 'Settings',
    avgSalary: '₹7-12L',
    keySkills: ['Docker', 'Python', 'Git', 'CI/CD', 'MLflow', 'Basic ML'],
    demandLevel: 'medium',
    experienceRange: '1-3 years',
    isLive: false,
    tier: 'junior'
  },
  {
    id: 'mlops-engineer',
    title: 'MLOps Engineer',
    category: 'ai-ml',
    description: 'Deploy and maintain ML systems in production at scale',
    icon: 'Settings',
    avgSalary: '₹14-28L',
    keySkills: ['Kubernetes', 'Docker', 'MLflow', 'Terraform', 'CI/CD', 'AWS/GCP'],
    demandLevel: 'high',
    experienceRange: '3-7 years',
    isLive: false,
    tier: 'mid-to-senior'
  },
  {
    id: 'rag-engineer',
    title: 'RAG Engineer',
    category: 'ai-ml',
    description: 'Build Retrieval Augmented Generation systems',
    icon: 'Search',
    avgSalary: '₹10-18L',
    keySkills: ['RAG', 'Vector Databases', 'LangChain', 'Embeddings', 'Python', 'LLMs'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },

  // ========================================
  // DATA & ANALYTICS (6 roles)
  // ========================================
  {
    id: 'data-scientist',
    title: 'Data Scientist',
    category: 'data',
    description: 'Extract insights from data using statistical analysis and ML',
    icon: 'TrendingUp',
    avgSalary: '₹8-16L',
    keySkills: ['Python', 'Statistics', 'ML', 'SQL', 'Pandas', 'Visualization'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'data-engineer',
    title: 'Data Engineer',
    category: 'data',
    description: 'Build and maintain data pipelines and infrastructure',
    icon: 'Database',
    avgSalary: '₹9-17L',
    keySkills: ['SQL', 'Python', 'Spark', 'Airflow', 'AWS/GCP', 'ETL'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'junior-data-analyst',
    title: 'Junior Data Analyst',
    category: 'data',
    description: 'Entry-level data analysis and reporting role',
    icon: 'BarChart2',
    avgSalary: '₹4-8L',
    keySkills: ['SQL', 'Excel', 'Python', 'Tableau', 'Power BI', 'Statistics'],
    demandLevel: 'medium',
    experienceRange: '0-2 years',
    isLive: false,
    tier: 'junior'
  },
  {
    id: 'analytics-engineer',
    title: 'Analytics Engineer',
    category: 'data',
    description: 'Transform and model data for analytics',
    icon: 'GitMerge',
    avgSalary: '₹8-15L',
    keySkills: ['dbt', 'SQL', 'Python', 'Data Modeling', 'BigQuery', 'Snowflake'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'associate-data-architect',
    title: 'Associate Data Architect',
    category: 'data',
    description: 'Mid-level data architecture and design role',
    icon: 'Layers',
    avgSalary: '₹9-16L',
    keySkills: ['Data Modeling', 'SQL', 'ETL', 'Cloud', 'BigQuery', 'Snowflake'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'data-architect',
    title: 'Data Architect',
    category: 'data',
    description: 'Design enterprise-scale data architecture',
    icon: 'Layers',
    avgSalary: '₹20-45L',
    keySkills: ['Data Architecture', 'Data Governance', 'Cloud', 'Spark', 'Kafka', 'System Design'],
    demandLevel: 'high',
    experienceRange: '5-10 years',
    isLive: false,
    tier: 'senior'
  },

  // ========================================
  // FRONTEND (6 roles)
  // ========================================
  {
    id: 'react-developer',
    title: 'React Developer',
    category: 'frontend',
    description: 'Build modern web applications using React',
    icon: 'Code2',
    avgSalary: '₹6-13L',
    keySkills: ['React', 'JavaScript', 'TypeScript', 'Redux', 'Next.js', 'Tailwind'],
    demandLevel: 'high',
    experienceRange: '1-4 years',
    isLive: false,
    tier: 'junior-to-mid'
  },
  {
    id: 'frontend-developer',
    title: 'Frontend Developer',
    category: 'frontend',
    description: 'Create responsive and interactive user interfaces',
    icon: 'Monitor',
    avgSalary: '₹5-12L',
    keySkills: ['HTML', 'CSS', 'JavaScript', 'React', 'Vue', 'TypeScript'],
    demandLevel: 'high',
    experienceRange: '1-4 years',
    isLive: false,
    tier: 'junior-to-mid'
  },
  {
    id: 'angular-developer',
    title: 'Angular Developer',
    category: 'frontend',
    description: 'Build enterprise web applications with Angular',
    icon: 'Code',
    avgSalary: '₹6-13L',
    keySkills: ['Angular', 'TypeScript', 'RxJS', 'NgRx', 'HTML', 'CSS'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'vuejs-developer',
    title: 'Vue.js Developer',
    category: 'frontend',
    description: 'Develop web apps using Vue.js framework',
    icon: 'Code',
    avgSalary: '₹5.5-12L',
    keySkills: ['Vue.js', 'JavaScript', 'Vuex', 'Nuxt.js', 'TypeScript', 'HTML/CSS'],
    demandLevel: 'medium',
    experienceRange: '1-4 years',
    isLive: false,
    tier: 'junior-to-mid'
  },
  {
    id: 'nextjs-developer',
    title: 'Next.js Developer',
    category: 'frontend',
    description: 'Build full-stack React applications with Next.js',
    icon: 'Zap',
    avgSalary: '₹7-15L',
    keySkills: ['Next.js', 'React', 'TypeScript', 'SSR', 'API Routes', 'Vercel'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'ui-developer',
    title: 'UI Developer',
    category: 'frontend',
    description: 'Specialize in user interface development',
    icon: 'Palette',
    avgSalary: '₹4.5-10L',
    keySkills: ['HTML', 'CSS', 'JavaScript', 'SASS', 'Tailwind', 'Figma'],
    demandLevel: 'medium',
    experienceRange: '1-4 years',
    isLive: false,
    tier: 'junior'
  },

  // ========================================
  // BACKEND (8 roles)
  // ========================================
  {
    id: 'python-developer',
    title: 'Python Developer',
    category: 'backend',
    description: 'Build backend systems and APIs with Python',
    icon: 'Code2',
    avgSalary: '₹6-14L',
    keySkills: ['Python', 'Django', 'Flask', 'FastAPI', 'PostgreSQL', 'REST APIs'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: true,
    tier: 'mid'
  },
  {
    id: 'backend-developer',
    title: 'Backend Developer',
    category: 'backend',
    description: 'Build scalable server-side applications and APIs',
    icon: 'Server',
    avgSalary: '₹6-14L',
    keySkills: ['Node.js', 'Python', 'Java', 'PostgreSQL', 'MongoDB', 'AWS'],
    demandLevel: 'high',
    experienceRange: '1-4 years',
    isLive: true,
    tier: 'junior-to-mid'
  },
  {
    id: 'nodejs-developer',
    title: 'Node.js Developer',
    category: 'backend',
    description: 'Build JavaScript backend applications',
    icon: 'Package',
    avgSalary: '₹6-14L',
    keySkills: ['Node.js', 'Express', 'TypeScript', 'MongoDB', 'PostgreSQL', 'REST APIs'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: true,
    tier: 'mid'
  },
  {
    id: 'django-developer',
    title: 'Django Developer',
    category: 'backend',
    description: 'Build web applications with Django framework',
    icon: 'Code',
    avgSalary: '₹6-14L',
    keySkills: ['Django', 'Python', 'PostgreSQL', 'REST APIs', 'Docker', 'Celery'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'fastapi-developer',
    title: 'FastAPI Developer',
    category: 'backend',
    description: 'Build high-performance APIs with FastAPI',
    icon: 'Zap',
    avgSalary: '₹7-15L',
    keySkills: ['FastAPI', 'Python', 'Async', 'Pydantic', 'PostgreSQL', 'Docker'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'java-developer',
    title: 'Java Developer',
    category: 'backend',
    description: 'Develop enterprise Java applications',
    icon: 'Coffee',
    avgSalary: '₹6-14L',
    keySkills: ['Java', 'Spring', 'Hibernate', 'SQL', 'Maven', 'JUnit'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'spring-boot-developer',
    title: 'Spring Boot Developer',
    category: 'backend',
    description: 'Build microservices with Spring Boot',
    icon: 'Layers',
    avgSalary: '₹7-15L',
    keySkills: ['Spring Boot', 'Java', 'Microservices', 'REST APIs', 'MySQL', 'Docker'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'go-developer',
    title: 'Go Developer',
    category: 'backend',
    description: 'Build high-performance services with Go',
    icon: 'Zap',
    avgSalary: '₹8-16L',
    keySkills: ['Go', 'Microservices', 'gRPC', 'Docker', 'Kubernetes', 'PostgreSQL'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },

  // ========================================
  // FULL STACK (4 roles)
  // ========================================
  {
    id: 'full-stack-developer',
    title: 'Full Stack Developer',
    category: 'fullstack',
    description: 'Build complete web applications end-to-end',
    icon: 'Code',
    avgSalary: '₹7-15L',
    keySkills: ['React', 'Node.js', 'MongoDB', 'PostgreSQL', 'TypeScript', 'AWS'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: true,
    tier: 'mid'
  },
  {
    id: 'software-developer',
    title: 'Software Developer',
    category: 'fullstack',  // Or 'backend' - your choice
    description: 'General software development across frontend and backend',
    icon: 'Code',
    avgSalary: '₹6-14L',
    keySkills: ['JavaScript', 'Python', 'React', 'Node.js', 'SQL', 'Git'],
    demandLevel: 'high',
    experienceRange: '1-4 years',
    isLive: false,
    tier: 'junior-to-mid'
  },
  {
    id: 'mern-stack-developer',
    title: 'MERN Stack Developer',
    category: 'fullstack',
    description: 'Specialize in MongoDB, Express, React, Node.js stack',
    icon: 'Layers',
    avgSalary: '₹6-14L',
    keySkills: ['MongoDB', 'Express', 'React', 'Node.js', 'JavaScript', 'TypeScript'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'mean-stack-developer',
    title: 'MEAN Stack Developer',
    category: 'fullstack',
    description: 'Build apps with MongoDB, Express, Angular, Node.js',
    icon: 'Layers',
    avgSalary: '₹6.5-14L',
    keySkills: ['MongoDB', 'Express', 'Angular', 'Node.js', 'TypeScript', 'Docker'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },

  // ========================================
  // MOBILE (5 roles)
  // ========================================
  {
    id: 'android-developer',
    title: 'Android Developer',
    category: 'mobile',
    description: 'Build native Android applications',
    icon: 'Smartphone',
    avgSalary: '₹6-14L',
    keySkills: ['Android', 'Kotlin', 'Java', 'Jetpack Compose', 'Firebase', 'Material Design'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'ios-developer',
    title: 'iOS Developer',
    category: 'mobile',
    description: 'Develop native iOS applications',
    icon: 'Smartphone',
    avgSalary: '₹7-15L',
    keySkills: ['iOS', 'Swift', 'SwiftUI', 'UIKit', 'Xcode', 'Core Data'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'react-native-developer',
    title: 'React Native Developer',
    category: 'mobile',
    description: 'Build cross-platform mobile apps with React Native',
    icon: 'Smartphone',
    avgSalary: '₹6-13L',
    keySkills: ['React Native', 'JavaScript', 'TypeScript', 'Redux', 'Firebase', 'Expo'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'flutter-developer',
    title: 'Flutter Developer',
    category: 'mobile',
    description: 'Create cross-platform apps with Flutter',
    icon: 'Smartphone',
    avgSalary: '₹6.5-14L',
    keySkills: ['Flutter', 'Dart', 'Firebase', 'State Management', 'Material Design', 'REST APIs'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'mobile-app-developer',
    title: 'Mobile App Developer',
    category: 'mobile',
    description: 'General mobile application development',
    icon: 'Smartphone',
    avgSalary: '₹6-13L',
    keySkills: ['React Native', 'Flutter', 'iOS', 'Android', 'JavaScript', 'Firebase'],
    demandLevel: 'medium',
    experienceRange: '1-4 years',
    isLive: false,
    tier: 'junior-to-mid'
  },

  // ========================================
  // CLOUD & DEVOPS (11 roles)
  // ========================================
  {
    id: 'junior-devops-engineer',
    title: 'Junior DevOps Engineer',
    category: 'cloud-devops',
    description: 'Entry-level DevOps and infrastructure role',
    icon: 'Cloud',
    avgSalary: '₹5-9L',
    keySkills: ['Docker', 'Linux', 'Git', 'CI/CD', 'Bash', 'AWS/GCP'],
    demandLevel: 'medium',
    experienceRange: '0-2 years',
    isLive: false,
    tier: 'junior'
  },
  {
    id: 'devops-engineer',
    title: 'DevOps Engineer',
    category: 'cloud-devops',
    description: 'Automate infrastructure and deployment pipelines',
    icon: 'GitBranch',
    avgSalary: '₹8-16L',
    keySkills: ['Docker', 'Kubernetes', 'CI/CD', 'Terraform', 'AWS', 'Linux'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'cloud-engineer',
    title: 'Cloud Engineer',
    category: 'cloud-devops',
    description: 'Design and manage cloud infrastructure',
    icon: 'Cloud',
    avgSalary: '₹9-17L',
    keySkills: ['AWS', 'Azure', 'GCP', 'Terraform', 'Kubernetes', 'Networking'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'aws-developer',
    title: 'AWS Developer',
    category: 'cloud-devops',
    description: 'Build cloud-native applications on AWS',
    icon: 'CloudLightning',
    avgSalary: '₹8-16L',
    keySkills: ['AWS', 'Lambda', 'S3', 'EC2', 'DynamoDB', 'Python/Node.js'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'kubernetes-engineer',
    title: 'Kubernetes Engineer',
    category: 'cloud-devops',
    description: 'Manage container orchestration at scale',
    icon: 'Box',
    avgSalary: '₹10-20L',
    keySkills: ['Kubernetes', 'Docker', 'Helm', 'Terraform', 'Service Mesh', 'Monitoring'],
    demandLevel: 'high',
    experienceRange: '3-6 years',
    isLive: false,
    tier: 'mid-to-senior'
  },
  {
    id: 'sre',
    title: 'SRE',
    category: 'cloud-devops',
    description: 'Ensure system reliability and performance',
    icon: 'Shield',
    avgSalary: '₹15-35L',
    keySkills: ['SRE', 'Kubernetes', 'Monitoring', 'Incident Management', 'Go', 'Terraform'],
    demandLevel: 'high',
    experienceRange: '3-8 years',
    isLive: false,
    tier: 'senior'
  },
  {
    id: 'cloud-security-engineer',
    title: 'Cloud Security Engineer',
    category: 'cloud-devops',
    description: 'Secure cloud infrastructure and applications',
    icon: 'ShieldCheck',
    avgSalary: '₹10-20L',
    keySkills: ['Cloud Security', 'AWS/Azure/GCP', 'IAM', 'Compliance', 'Python', 'SIEM'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'senior-cloud-security-engineer',
    title: 'Senior Cloud Security Engineer',
    category: 'cloud-devops',
    description: 'Lead cloud security strategy and implementation',
    icon: 'ShieldAlert',
    avgSalary: '₹18-35L',
    keySkills: ['Zero Trust', 'Threat Modeling', 'Security Architecture', 'IAM', 'DevSecOps', 'Python'],
    demandLevel: 'high',
    experienceRange: '5-10 years',
    isLive: false,
    tier: 'senior'
  },
  {
    id: 'cicd-engineer',
    title: 'CI/CD Engineer',
    category: 'cloud-devops',
    description: 'Build and maintain continuous delivery pipelines',
    icon: 'GitMerge',
    avgSalary: '₹8-15L',
    keySkills: ['CI/CD', 'Jenkins', 'GitLab CI', 'GitHub Actions', 'Docker', 'Terraform'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'infrastructure-engineer',
    title: 'Infrastructure Engineer',
    category: 'cloud-devops',
    description: 'Manage and automate infrastructure',
    icon: 'Network',
    avgSalary: '₹9-17L',
    keySkills: ['Terraform', 'Ansible', 'Linux', 'Networking', 'Cloud', 'Monitoring'],
    demandLevel: 'medium',
    experienceRange: '3-6 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'azure-developer',
    title: 'Azure Developer',
    category: 'cloud-devops',
    description: 'Build applications on Microsoft Azure',
    icon: 'Cloud',
    avgSalary: '₹7.5-15L',
    keySkills: ['Azure', 'Azure Functions', 'C#', '.NET', 'Azure DevOps', 'PowerShell'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },

  // ========================================
  // EMERGING TECH (8 roles)
  // ========================================
  {
    id: 'blockchain-developer',
    title: 'Blockchain Developer',
    category: 'emerging',
    description: 'Build decentralized applications and smart contracts',
    icon: 'Link',
    avgSalary: '₹12-25L',
    keySkills: ['Solidity', 'Ethereum', 'Web3.js', 'Smart Contracts', 'DeFi', 'JavaScript'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid-to-senior'
  },
  {
    id: 'web3-developer',
    title: 'Web3 Developer',
    category: 'emerging',
    description: 'Develop decentralized web applications',
    icon: 'Globe',
    avgSalary: '₹10-22L',
    keySkills: ['Web3', 'Ethereum', 'Solidity', 'React', 'MetaMask', 'DApps'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'game-developer',
    title: 'Game Developer',
    category: 'emerging',
    description: 'Create games and interactive experiences',
    icon: 'Gamepad2',
    avgSalary: '₹6-14L',
    keySkills: ['Unity', 'Unreal Engine', 'C#', 'C++', 'Game Design', '3D Math'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'ar-vr-developer',
    title: 'AR/VR Developer',
    category: 'emerging',
    description: 'Build augmented and virtual reality applications',
    icon: 'Glasses',
    avgSalary: '₹8-16L',
    keySkills: ['AR', 'VR', 'Unity', 'ARKit', 'ARCore', 'C#', '3D Modeling'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'iot-engineer',
    title: 'IoT Engineer',
    category: 'emerging',
    description: 'Develop Internet of Things solutions',
    icon: 'Wifi',
    avgSalary: '₹6.5-14L',
    keySkills: ['IoT', 'Embedded Systems', 'Python', 'MQTT', 'Sensors', 'Cloud'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'embedded-systems-engineer',
    title: 'Embedded Systems Engineer',
    category: 'emerging',
    description: 'Design embedded software for hardware systems',
    icon: 'Cpu',
    avgSalary: '₹6-13L',
    keySkills: ['Embedded C', 'C++', 'Microcontrollers', 'RTOS', 'ARM', 'Hardware'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'conversational-ai-developer',
    title: 'Conversational AI Developer',
    category: 'emerging',
    description: 'Build chatbots and voice assistants',
    icon: 'MessageCircle',
    avgSalary: '₹7-15L',
    keySkills: ['Conversational AI', 'Chatbots', 'NLP', 'Dialogflow', 'LLMs', 'Python'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'ai-application-developer',
    title: 'AI Application Developer',
    category: 'emerging',
    description: 'Build AI-powered applications',
    icon: 'Sparkles',
    avgSalary: '₹8-16L',
    keySkills: ['AI Integration', 'Python', 'LLMs', 'OpenAI', 'React', 'Full Stack'],
    demandLevel: 'high',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },

  // ========================================
  // SPECIALIZED (7 roles)
  // ========================================
  {
    id: 'uiux-developer',
    title: 'UI/UX Developer',
    category: 'other',
    description: 'Design and implement user experiences',
    icon: 'Palette',
    avgSalary: '₹6-13L',
    keySkills: ['UI/UX', 'Figma', 'HTML', 'CSS', 'React', 'Design Systems'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'api-developer',
    title: 'API Developer',
    category: 'other',
    description: 'Design and build RESTful and GraphQL APIs',
    icon: 'Network',
    avgSalary: '₹6-13L',
    keySkills: ['REST APIs', 'GraphQL', 'Node.js', 'Python', 'API Design', 'Documentation'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'graphql-developer',
    title: 'GraphQL Developer',
    category: 'other',
    description: 'Build GraphQL APIs and schemas',
    icon: 'GitGraph',
    avgSalary: '₹7-15L',
    keySkills: ['GraphQL', 'Apollo', 'Node.js', 'React', 'Schema Design', 'TypeScript'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'microservices-developer',
    title: 'Microservices Developer',
    category: 'other',
    description: 'Design and implement microservices architecture',
    icon: 'Box',
    avgSalary: '₹8-16L',
    keySkills: ['Microservices', 'Docker', 'Kubernetes', 'Event-Driven', 'Message Queues', 'Cloud'],
    demandLevel: 'high',
    experienceRange: '3-6 years',
    isLive: false,
    tier: 'mid-to-senior'
  },
  {
    id: 'security-engineer',
    title: 'Security Engineer',
    category: 'other',
    description: 'Protect systems and applications from threats',
    icon: 'ShieldAlert',
    avgSalary: '₹9-18L',
    keySkills: ['Security', 'Penetration Testing', 'OWASP', 'Linux', 'Python', 'Compliance'],
    demandLevel: 'high',
    experienceRange: '3-6 years',
    isLive: false,
    tier: 'mid-to-senior'
  },
  {
    id: 'design-engineer',
    title: 'Design Engineer',
    category: 'other',
    description: 'Bridge design and development',
    icon: 'Pen Tool',
    avgSalary: '₹7-15L',
    keySkills: ['Design', 'Figma', 'React', 'CSS', 'Prototyping', 'Animation'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  },
  {
    id: 'frontend-ui-engineer',
    title: 'Frontend UI Engineer',
    category: 'other',
    description: 'Specialize in component libraries and design systems',
    icon: 'Layers',
    avgSalary: '₹6.5-14L',
    keySkills: ['HTML', 'CSS', 'JavaScript', 'React', 'Storybook', 'Component Libraries'],
    demandLevel: 'medium',
    experienceRange: '2-5 years',
    isLive: false,
    tier: 'mid'
  }
];

// Helper function to get roles by category
export const getRolesByCategory = (categoryId) => {
  return JOB_ROLES.filter(role => role.category === categoryId);
};

// Helper function to check if role has live data
export const isLiveRole = (roleTitle) => {
  return LIVE_API_ROLES.includes(roleTitle);
};

// Get role count by category
export const getRoleCounts = () => {
  const counts = {};
  JOB_CATEGORIES.forEach(category => {
    counts[category.id] = getRolesByCategory(category.id).length;
  });
  return counts;
};